import { useState } from 'react';
import { fmt, fmtPct, fmtRatePct } from '../../lib/nmtc/calculations';

/* ── Toggle switch ─────────────────────────────────────────── */
function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
        checked ? 'bg-emerald-600' : 'bg-slate-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform mt-0.5 ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

/* ── Stat card ─────────────────────────────────────────────── */
function StatCard({ label, value, sublabel, accent = false, negative = false }) {
  return (
    <div className={`rounded-xl px-5 py-4 ${accent ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-white border border-slate-200'}`}>
      <p className={`text-xs font-medium mb-1 ${accent ? 'text-emerald-600' : 'text-slate-500'}`}>{label}</p>
      <p className={`text-2xl font-bold tracking-tight ${negative ? 'text-red-600' : accent ? 'text-emerald-800' : 'text-slate-900'}`}>
        {value}
      </p>
      {sublabel && <p className="text-xs text-slate-400 mt-0.5">{sublabel}</p>}
    </div>
  );
}

/* ── Section wrapper ───────────────────────────────────────── */
function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

/* ── Main component ────────────────────────────────────────── */
export default function NetBenefitTab({ calc, inputs }) {
  const [useNPV, setUseNPV] = useState(false);
  const [feeEnabled, setFeeEnabled] = useState({
    subAlloc: true, consultant: true, attorney: true,
    accountant: true, title: true, bond: true, assetMgmt: true,
  });
  const [customFees, setCustomFees] = useState([]);
  const [newFeeLabel, setNewFeeLabel] = useState('');
  const [newFeeAmount, setNewFeeAmount] = useState('');
  const [showFeeManager, setShowFeeManager] = useState(true);

  const grossBenefit = useNPV ? calc.npvBNoteForgiveness : calc.bNote;

  const FEE_LINES = [
    { key: 'subAlloc',   label: 'Sub-Allocation Fee',   sublabel: `${inputs.subAllocationPct.toFixed(2)}% of QEI`,     amount: calc.subAllocationFee,    color: '#f87171' },
    { key: 'consultant', label: 'NM Consultant Fee',    sublabel: '1.00% of QEI',                                      amount: calc.nmtcConsultantFee,   color: '#fb923c' },
    { key: 'attorney',   label: 'Attorney Fees',        sublabel: 'Legal + structuring + closing',                     amount: calc.attorneyFees,        color: '#f59e0b' },
    { key: 'accountant', label: 'Accountant Fees',      sublabel: 'NMTC accounting + tax opinion',                     amount: calc.accountantFees,      color: '#eab308' },
    { key: 'title',      label: 'Title Insurance',      sublabel: '~0.15% of leverage loan',                           amount: calc.titleInsurance,      color: '#ca8a04' },
    { key: 'bond',       label: 'Performance Bond',     sublabel: '~0.75% of leverage loan',                           amount: calc.performanceBondFees, color: '#ea580c' },
    { key: 'assetMgmt',  label: 'Asset Management',     sublabel: useNPV ? '0.5%/yr — NPV @ 5%' : '0.5%/yr × 7 yrs', amount: useNPV ? calc.npvAssetMgmtFees : calc.assetMgmtFeeTotal, color: '#a855f7' },
  ];

  const activeFeeSum =
    FEE_LINES.reduce((sum, f) => sum + (feeEnabled[f.key] ? f.amount : 0), 0) +
    customFees.reduce((sum, f) => sum + f.amount, 0);

  const netSubsidy = grossBenefit - activeFeeSum;
  const subsidyPctOfQEI = calc.qei > 0 ? netSubsidy / calc.qei : 0;

  const addCustomFee = () => {
    const amt = parseFloat(newFeeAmount);
    if (!newFeeLabel.trim() || isNaN(amt) || amt <= 0) return;
    setCustomFees((prev) => [...prev, { id: Date.now().toString(), label: newFeeLabel.trim(), amount: amt }]);
    setNewFeeLabel('');
    setNewFeeAmount('');
  };

  const chartTotal = grossBenefit;
  const netPct = chartTotal > 0 ? Math.max((netSubsidy / chartTotal) * 100, 1) : 0;

  const annualNMTC = calc.totalAnnualNMTCService + calc.assetMgmtFeeAnnual;
  const annualSavings = calc.convFullQEIAnnual - annualNMTC;

  return (
    <div className="space-y-10">

      {/* ── Hero Stats ─────────────────────────────────────── */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-1">Net Benefit</p>
            <div className="flex items-baseline gap-2">
              <p className="text-5xl font-extrabold text-emerald-900">{fmtPct(subsidyPctOfQEI)}</p>
              <p className="text-base font-semibold text-emerald-700">of QEI</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-4 py-2.5 shadow-sm">
            <span className={`text-sm font-medium ${!useNPV ? 'text-emerald-700' : 'text-slate-400'}`}>Nominal</span>
            <Toggle checked={useNPV} onChange={setUseNPV} />
            <span className={`text-sm font-medium ${useNPV ? 'text-emerald-700' : 'text-slate-400'}`}>NPV</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Gross Benefit (B-Note Forgiveness)" value={fmt(grossBenefit)} sublabel={useNPV ? 'Discounted at 5%' : 'Face value at Year 7'} />
          <StatCard label="Total Transaction Costs" value={fmt(activeFeeSum)} negative />
          <StatCard label="Net Subsidy" value={fmt(netSubsidy)} sublabel={`${fmtPct(netSubsidy / grossBenefit)} of gross retained`} accent />
        </div>
      </section>

      {/* ── Subsidy Waterfall Bar ──────────────────────────── */}
      <section>
        <div className="rounded-xl overflow-hidden h-12 flex shadow-inner border border-slate-200">
          {netSubsidy > 0 && (
            <div
              style={{ width: `${netPct}%`, backgroundColor: '#10b981' }}
              className="flex items-center justify-center overflow-hidden transition-all"
              title={`Net Subsidy: ${fmt(netSubsidy)}`}
            >
              <span className="text-white text-sm font-bold px-2 truncate">Net {fmt(netSubsidy)}</span>
            </div>
          )}
          {FEE_LINES.filter(f => feeEnabled[f.key]).map((f) => (
            <div
              key={f.key}
              style={{ width: `${(f.amount / chartTotal) * 100}%`, backgroundColor: f.color }}
              title={`${f.label}: ${fmt(f.amount)}`}
              className="flex items-center justify-center overflow-hidden"
            >
              <span className="text-white text-xs font-semibold px-1 truncate hidden sm:block">{f.label.split(' ')[0]}</span>
            </div>
          ))}
          {customFees.map((f) => (
            <div
              key={f.id}
              style={{ width: `${(f.amount / chartTotal) * 100}%`, backgroundColor: '#94a3b8' }}
              title={`${f.label}: ${fmt(f.amount)}`}
              className="overflow-hidden"
            />
          ))}
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-500 mt-2.5">
          <span>Gross: <span className="font-semibold text-slate-700">{fmt(grossBenefit)}</span></span>
          <span className="text-slate-300">→</span>
          <span>Fees: <span className="font-semibold text-red-600">{fmt(activeFeeSum)}</span></span>
          <span className="text-slate-300">→</span>
          <span>Net: <span className="font-bold text-emerald-700">{fmt(netSubsidy)}</span></span>
        </div>
      </section>

      {/* ── With vs. Without NMTC ──────────────────────────── */}
      <Section
        title="With NMTC vs. Without"
        subtitle={`7-year comparison: NMTC structure vs. conventional ${inputs.hardDebtRate.toFixed(2)}% / ${inputs.loanTerm}-year amortizing loan on ${fmt(calc.qei)}.`}
      >
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-[38%]"></th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Without NMTC</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-emerald-700 uppercase tracking-wide bg-emerald-50/50">With NMTC</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-emerald-700 uppercase tracking-wide bg-emerald-50/50">Savings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="px-5 py-3 text-slate-700">Total financing</td>
                <td className="px-5 py-3 text-right font-mono text-slate-800">{fmt(calc.qei)}</td>
                <td className="px-5 py-3 text-right font-mono text-slate-800 bg-emerald-50/20">{fmt(calc.qei)}</td>
                <td className="px-5 py-3 text-right font-mono text-slate-400 bg-emerald-50/20">—</td>
              </tr>
              <tr className="bg-slate-50/50">
                <td className="px-5 py-3 text-slate-700">Annual payment</td>
                <td className="px-5 py-3 text-right font-mono text-slate-800">{fmt(calc.convFullQEIAnnual)}</td>
                <td className="px-5 py-3 text-right font-mono text-emerald-800 bg-emerald-50/20">{fmt(annualNMTC)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700 bg-emerald-50/20">{fmt(annualSavings)}</td>
              </tr>
              <tr>
                <td className="px-5 py-3 text-slate-700">Monthly payment</td>
                <td className="px-5 py-3 text-right font-mono text-slate-800">{fmt(calc.convFullQEIAnnual / 12)}</td>
                <td className="px-5 py-3 text-right font-mono text-emerald-800 bg-emerald-50/20">{fmt(annualNMTC / 12)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700 bg-emerald-50/20">{fmt(annualSavings / 12)}</td>
              </tr>
              <tr className="bg-slate-50/50">
                <td className="px-5 py-3 text-slate-700">NMTC closing fees</td>
                <td className="px-5 py-3 text-right font-mono text-slate-400">—</td>
                <td className="px-5 py-3 text-right font-mono text-red-600 bg-emerald-50/20">{fmt(calc.totalClosingFees)}</td>
                <td className="px-5 py-3 text-right font-mono text-red-500 bg-emerald-50/20">({fmt(calc.totalClosingFees)})</td>
              </tr>
              <tr>
                <td className="px-5 py-3 font-semibold text-slate-800">7-year total cost</td>
                <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">{fmt(calc.convFullQEI7Years)}</td>
                <td className="px-5 py-3 text-right font-mono font-bold text-slate-900 bg-emerald-50/20">{fmt(calc.nmtcTotal7YearCost)}</td>
                <td className="px-5 py-3 text-right font-mono font-bold text-emerald-700 bg-emerald-50/20">{fmt(calc.convFullQEI7Years - calc.nmtcTotal7YearCost)}</td>
              </tr>
              <tr className="bg-slate-50/50">
                <td className="px-5 py-3 text-slate-700">Debt forgiven at Year 7</td>
                <td className="px-5 py-3 text-right font-mono text-slate-400">$0</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700 bg-emerald-50/20">+ {fmt(calc.bNote)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700 bg-emerald-50/20">+ {fmt(calc.bNote)}</td>
              </tr>
              <tr>
                <td className="px-5 py-3 text-slate-700">Balance remaining</td>
                <td className="px-5 py-3 text-right font-mono text-slate-500 text-xs">still amortizing</td>
                <td className="px-5 py-3 text-right font-mono text-amber-700 bg-emerald-50/20">{fmt(calc.aNote)}</td>
                <td className="px-5 py-3 text-right bg-emerald-50/20"></td>
              </tr>
              <tr className="bg-slate-50/50">
                <td className="px-5 py-3 text-slate-700">Effective rate</td>
                <td className="px-5 py-3 text-right font-mono text-slate-800">{inputs.hardDebtRate.toFixed(2)}%</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700 bg-emerald-50/20">{fmtRatePct(calc.effectiveRate)}</td>
                <td className="px-5 py-3 text-right bg-emerald-50/20"></td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="bg-emerald-700 text-white">
                <td className="px-5 py-4 font-bold text-base" colSpan={3}>Total 7-Year Benefit</td>
                <td className="px-5 py-4 text-right font-mono text-xl font-bold">{fmt(calc.nmtcSavings7Year)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-sm text-slate-500 leading-relaxed mt-2">
          Save <strong className="text-emerald-700">{fmt(annualSavings)}/yr</strong> in debt service ({fmt(annualSavings / 12)}/mo).
          The <strong className="text-emerald-700">{fmt(calc.bNote)}</strong> B-Note converts to permanent equity at Year 7.
        </p>
      </Section>

      {/* ── Cost Items ─────────────────────────────────────── */}
      <Section title="Transaction Costs" subtitle="Toggle individual fees to stress-test the economics.">
        <div className="flex items-center justify-end mb-1">
          <button
            onClick={() => setShowFeeManager((o) => !o)}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
          >
            {showFeeManager ? '▲ Collapse' : '▼ Expand'}
          </button>
        </div>

        {showFeeManager && (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Fee</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Amount</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">% QEI</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide w-16">On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {FEE_LINES.map((f) => (
                  <tr
                    key={f.key}
                    className={`transition-opacity ${!feeEnabled[f.key] ? 'opacity-40' : ''} hover:bg-slate-50/80`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: f.color }} />
                        <div>
                          <p className="text-sm text-slate-800 font-medium">{f.label}</p>
                          <p className="text-xs text-slate-400">{f.sublabel}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-slate-700">{fmt(f.amount)}</td>
                    <td className="px-5 py-3 text-right text-xs text-slate-400">{fmtPct(f.amount / calc.qei)}</td>
                    <td className="px-5 py-3 text-center">
                      <Toggle
                        checked={feeEnabled[f.key]}
                        onChange={(v) => setFeeEnabled((prev) => ({ ...prev, [f.key]: v }))}
                      />
                    </td>
                  </tr>
                ))}

                {customFees.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-50/80">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2.5 h-2.5 rounded-sm bg-slate-400 shrink-0" />
                        <p className="text-sm text-slate-800 font-medium">{f.label} <span className="text-xs text-slate-400">(custom)</span></p>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-slate-700">{fmt(f.amount)}</td>
                    <td className="px-5 py-3 text-right text-xs text-slate-400">{fmtPct(f.amount / calc.qei)}</td>
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => setCustomFees((prev) => prev.filter((x) => x.id !== f.id))}
                        className="text-xs text-red-400 hover:text-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td className="px-5 py-3 font-bold text-slate-800">Total Active Fees</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-red-600">{fmt(activeFeeSum)}</td>
                  <td className="px-5 py-3 text-right text-xs font-semibold text-slate-600">{fmtPct(activeFeeSum / calc.qei)}</td>
                  <td className="px-5 py-3"></td>
                </tr>
              </tfoot>
            </table>

            <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-500 mb-2">Add a custom cost item</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Label (e.g. Developer Fee)"
                  value={newFeeLabel}
                  onChange={(e) => setNewFeeLabel(e.target.value)}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                  <span className="px-2.5 text-slate-400 text-sm bg-slate-50">$</span>
                  <input
                    type="number"
                    placeholder="Amount"
                    min={0}
                    value={newFeeAmount}
                    onChange={(e) => setNewFeeAmount(e.target.value)}
                    className="w-28 px-2 py-2 text-sm text-slate-800 focus:outline-none"
                  />
                </div>
                <button
                  onClick={addCustomFee}
                  className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-lg hover:bg-emerald-800 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* ── 7-Year Tax Credit Schedule ─────────────────────── */}
      <Section title="7-Year Tax Credit Schedule">
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-emerald-800 text-white">
                <th className="px-5 py-3 text-left font-semibold">Year</th>
                <th className="px-5 py-3 text-right font-semibold">Credit Rate</th>
                <th className="px-5 py-3 text-right font-semibold">Annual Credits</th>
                <th className="px-5 py-3 text-right font-semibold">Cumulative</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {calc.creditSchedule.map((row, i) => (
                <tr key={row.year} className={i % 2 === 0 ? '' : 'bg-slate-50/50'}>
                  <td className="px-5 py-2.5 text-slate-700 font-medium">{row.year}</td>
                  <td className="px-5 py-2.5 text-right text-slate-600">{row.rate}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-700">{fmt(row.credits)}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-slate-700">{fmt(row.cumulative)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                <td className="px-5 py-3 text-slate-800">Total</td>
                <td className="px-5 py-3 text-right text-emerald-800">39%</td>
                <td className="px-5 py-3 text-right font-mono text-emerald-800">{fmt(calc.totalTaxCredits)}</td>
                <td className="px-5 py-3 text-right font-mono text-slate-800">{fmt(calc.totalTaxCredits)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Section>

      {/* ── 7-Year Debt Service Schedule ───────────────────── */}
      <Section
        title="7-Year Debt Service Schedule"
        subtitle={`NMTC: interest-only on both notes.  Conventional: fully amortizing ${inputs.loanTerm}-year loan at ${inputs.hardDebtRate.toFixed(2)}%.`}
      >
        <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[780px]">
            <thead>
              <tr className="bg-emerald-800 text-white">
                <th className="px-4 py-3 text-left font-semibold">Year</th>
                <th className="px-4 py-3 text-right font-semibold">A-Note (I/O)</th>
                <th className="px-4 py-3 text-right font-semibold">B-Note (I/O)</th>
                <th className="px-4 py-3 text-right font-semibold">NMTC Sub.</th>
                <th className="px-4 py-3 text-right font-semibold">Asset Mgmt</th>
                <th className="px-4 py-3 text-right font-semibold">Total Annual</th>
                <th className="px-4 py-3 text-right font-semibold bg-slate-600">Conventional</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {calc.debtServiceSchedule.map((row, i) => (
                <tr
                  key={row.year}
                  className={row.year === 7 ? 'bg-amber-50' : i % 2 === 0 ? '' : 'bg-slate-50/50'}
                >
                  <td className="px-4 py-2.5 text-slate-700 font-medium">{row.year}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-600">{fmt(row.aNotePayment)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-600">{fmt(row.bNotePayment)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmt(row.totalNMTCPayment)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-amber-700">{fmt(row.assetMgmtFee)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">{fmt(row.totalAnnualCost)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-500 bg-slate-50/50">{fmt(row.hardDebtAmortizing)}</td>
                </tr>
              ))}
              <tr className="bg-emerald-50">
                <td colSpan={7} className="px-4 py-3 border-t border-emerald-200">
                  <span className="text-sm font-semibold text-emerald-800">
                    Year 7 — B-Note Forgiven: <span className="font-bold">{fmt(calc.bNote)}</span>
                    <span className="font-normal text-emerald-600 ml-1">(NPV: {fmt(calc.npvBNoteForgiveness)})</span>
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                <td className="px-4 py-3 text-slate-800">7-Yr Total</td>
                <td className="px-4 py-3 text-right font-mono text-slate-800">{fmt(calc.annualANote * 7)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-800">{fmt(calc.annualBNote * 7)}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-800">{fmt(calc.totalNMTCService7Years)}</td>
                <td className="px-4 py-3 text-right font-mono text-amber-700">{fmt(calc.assetMgmtFeeTotal)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-900">{fmt(calc.totalNMTCService7Years + calc.assetMgmtFeeTotal)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-800 bg-slate-50">{fmt(calc.hardDebtAmortizingAnnual * 7)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Section>

      {/* ── How to read this ───────────────────────────────── */}
      <section className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-5">
        <p className="text-sm font-semibold text-slate-700 mb-1.5">How to read this</p>
        <p className="text-sm text-slate-600 leading-relaxed">
          The B-Note of <strong>{fmt(calc.bNote)}</strong> is forgiven at Year 7 — that is your gross benefit.
          Subtracting all transaction costs leaves a net subsidy of <strong>{fmt(netSubsidy)}</strong>{' '}
          ({fmtPct(subsidyPctOfQEI)} of QEI). Toggle individual cost items on or off above to stress-test
          the economics.
        </p>
      </section>
    </div>
  );
}
